<template>
  <div>
    <div
      v-if="profile.subscription"
      class="projects-buttons-grid"
      :items-count="countSubscribedProjectTypes"
    >
      <template
        v-if="profile.subscription &&
        !profile.subscription?.hideProjectOption &&
        profile.isSubUser"
      >
        <ProjectTypeProject />
      </template>
      <template
        v-if="profile.subscription &&
         !profile.subscription?.hideDataroomOption &&
         profile.isSubUser"
      >
        <ProjectTypeDataroom />
      </template>
      <template
        v-if="profile.subscription &&
        !profile.subscription?.hideArchiveOption"
      >
        <ProjectTypeArchive />
      </template>
    </div>

    <div class="my-9">
      <DocumentsBreadcrumb v-if="!this.projectsFolderByIdPending" :items="getProjectsFoldersPath" />
      <v-skeleton-loader v-else width="260px" type="text" />
    </div>

    <div class="d-flex align-center column-gap-4">
      <SearchTextField
        v-model="search"
        :tooltipText="$t('projects.ProjectsHeader.searchPlaceholder')"
        :loading="searchModeEnabled && (pmRoomsPending || guestRoomsPending)"
      />

      <app-button
        type="outlined"
        variant="neutral"
        @click="$store.commit('rooms/OPEN_FILTER_PANE')"
      >
        <app-material-icon v-if="!$vuetify.display.xs" name="instant_mix" color="inherit" />
        <span v-if="!$vuetify.display.xs">{{$t('projects.ProjectsHeader.filters')}}</span>
      </app-button>

      <div class="d-flex align-center ml-auto column-gap-4">
        <app-button v-if="(!currentProjectsFolder.id || currentProjectsFolder.depth < FOLDER_MAX_DEPTH) && !isRecentRoomsView && !searchModeEnabled"
          class="projects-header__create-new-folder"
          :class="{'ml-auto': !$vuetify.display.xs}"
          @click="openProjectsCreateNewFolder"
        >
          {{ $t('project.documents.dialogs.DocumentsNewFolderDialog.createFolder') }}
        </app-button>

        <app-segmented-control
          v-if="!$vuetify.display.mobile"
          :active-option="display"
          :options="displayOptions"
          data-locator="display-mode-segmented-control"
          @change-active-option="emitDisplayChoice"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'

import { FOLDER_MAX_DEPTH } from '@/common/constants'
import SearchTextField from '@/common/SearchTextField.vue'
import DocumentsBreadcrumb from '@/project/documents/DocumentsBreadcrumb.vue'
import { SET_IS_OPEN } from '@/store/modules/projects-create-new-folder/mutation_types'
import { SET_DISPLAY_TYPE } from '@/store/modules/rooms/mutation_types'

import { DISPLAY } from './display'
import ProjectTypeArchive from './project-types/ProjectTypeArchive.vue'
import ProjectTypeDataroom from './project-types/ProjectTypeDataroom.vue'
import ProjectTypeProject from './project-types/ProjectTypeProject.vue'

export default {
  name: 'ProjectsHeader',
  components: {
    SearchTextField,
    ProjectTypeProject,
    ProjectTypeDataroom,
    ProjectTypeArchive,
    DocumentsBreadcrumb,
  },
  props: {
    display: {
      required: true,
      validator: value => { return value in DISPLAY },
    },
    searchQuery: {
      type: String,
      required: true,
    },
    isRecentRoomsView: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      DISPLAY,
      FOLDER_MAX_DEPTH,
    }
  },
  computed: {
    ...mapState('projectsFolders', ['currentProjectsFolder']),
    ...mapState('rooms', ['sort', 'displayType', 'searchModeEnabled', 'pmRoomsPending', 'guestRoomsPending']),
    ...mapState('user', ['profile']),
    ...mapState('projectsFolders', ['projectsFoldersTree', 'currentProjectsFolder', 'projectsFolderByIdPending']),
    getProjectsFoldersPath () {
      const path = this.findProjectsFolderPath(this.projectsFoldersTree, this.currentProjectsFolder.parentId)
      let allPath = []
      if (this.isRecentRoomsView) {
        allPath = [{
          id: 'recent',
          name: this.$t('projects.ProjectsHeader.recent'),
        }]
      } else {
        allPath = [
          {
            id: undefined,
            name: this.$t('projects.ProjectsHeader.myProjects'),
          },
          ...path,
        ]
        if (this.currentProjectsFolder.id) {
          allPath.push(this.currentProjectsFolder)
        }
      }
      return allPath
    },
    search: {
      get () {
        return this.searchQuery
      },
      set (val) {
        this.$emit('update:searchQuery', val)
      },
    },
    displayOptions () {
      return [
        {
          icon: 'grid_view',
          locatorValue: 'grid-display-option',
          tooltip: this.$t('projects.ProjectsHeader.gridDisplay'),
          value: DISPLAY.grid,
        },
        {
          icon: 'list',
          locatorValue: 'table-display-option',
          tooltip: this.$t('projects.ProjectsHeader.tableDisplay'),
          value: DISPLAY.table,
        },
      ]
    },
    countSubscribedProjectTypes () {
      let count = 0
      if (this.profile.subscription) {
        if (!this.profile.subscription?.hideProjectOption && this.profile.isSubUser) {
          count++
        }
        if (!this.profile.subscription?.hideDataroomOption && this.profile.isSubUser) {
          count++
        }
        if (!this.profile.subscription?.hideArchiveOption) {
          count++
        }
      }
      return count
    },
  },
  methods: {
    ...mapMutations('rooms', [SET_DISPLAY_TYPE]),
    ...mapMutations('projectsCreateNewFolder', [SET_IS_OPEN]),
    openProjectsCreateNewFolder () {
      this.SET_IS_OPEN(true)
    },
    emitDisplayChoice (displayType) {
      this.SET_DISPLAY_TYPE(displayType)
      this.$emit('display', displayType)
    },
    findProjectsFolderPath (dossiers, parentId, path = []) {
      const parent = dossiers.find(dossier => dossier.id === parentId)
      if (!parent) {
        // If the parent folder is not found, returns an empty list
        return path
      }
      path.unshift(parent)

      // Otherwise, recursively searches for the parent folder
      return this.findProjectsFolderPath(dossiers, parent.parentId, path)
    },
  },
}
</script>

<style scoped lang="scss">
.project-header-icon {
  font-size: 24px;
}

.projects-buttons-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(max(220px, calc((100% - 32px) / 3)), 1fr));
  grid-auto-rows: 1fr;
  gap: 16px;

  & > :deep(div > button) {
    height: 100%;
  }
}
</style>
